import { requireOnlineAttentionRegister } from "../lib/attentionRegister";
import axios from "axios";
import { apiClient } from "./client";
//updated on 03-06-2026 again
import { openBlobInNewTab } from "../lib/openBlobInNewTab";
import type {
  PurchaseOrderApprovePayload,
  POBudgetResidualListParams,
  POBudgetResidualListResponse,
  PurchaseOrderComment,
  PurchaseOrderCommentCreatePayload,
  PurchaseOrderCancelAndRestartResult,
  PurchaseOrderCreateDirectPayload,
  PurchaseOrderCreateFromAwardsPayload,
  PurchaseOrderDetail,
  PurchaseOrderListQueryParams,
  PurchaseOrderListResponse,
  PurchaseOrderAccountCodeOptionsParams,
  PurchaseOrderAccountCodeOptionsResponse,
  PurchaseOrderRejectPayload,
  PurchaseOrderRevisePayload,
  PurchaseOrderRevisionSummary,
  PurchaseOrderSubmitPayload,
  PurchaseOrderUpdatePayload,
} from "../types/purchaseOrder";
import type { VesselId } from "../types/vessel";

interface DataEnvelope<TData> {
  data: TData;
}

interface PurchaseOrderReadOptions {
  invoiceId?: string;
  captureCandidate?: boolean;
}

interface PurchaseOrderSourceDocumentReadOptions extends PurchaseOrderReadOptions {
  fileName?: string;
}

function downloadBlob(blob: Blob, filename: string): void {
  const downloadUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = downloadUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(downloadUrl);
}

const PURCHASE_ORDER_SORT_VALUES = new Set([
  "created_desc",
  "created_asc",
  "po_number_asc",
  "po_number_desc",
  "purpose_asc",
  "purpose_desc",
  "vendor_asc",
  "vendor_desc",
]);

function normalizePurchaseOrderSortBy(sortBy: string | undefined): string {
  if (sortBy && PURCHASE_ORDER_SORT_VALUES.has(sortBy)) {
    return sortBy;
  }
  return "created_desc";
}

function compareNullableText(left: string | null | undefined, right: string | null | undefined): number {
  return String(left ?? "").localeCompare(String(right ?? ""), undefined, { sensitivity: "base" });
}

function createdTime(row: { created_at?: string | null }): number {
  const parsed = Date.parse(row.created_at ?? "");
  return Number.isNaN(parsed) ? 0 : parsed;
}

function isWithinCreatedDateRange(row: { created_at?: string | null }, dateFrom?: string, dateTo?: string): boolean {
  if (!dateFrom && !dateTo) {
    return true;
  }
  const createdDate = row.created_at?.slice(0, 10);
  if (!createdDate) {
    return false;
  }
  if (dateFrom && createdDate < dateFrom) {
    return false;
  }
  if (dateTo && createdDate > dateTo) {
    return false;
  }
  return true;
}

function comparePurchaseOrders(
  left: { id: string; po_number?: string | null; order_purpose?: string | null; vendor_name?: string | null; created_at?: string | null },
  right: { id: string; po_number?: string | null; order_purpose?: string | null; vendor_name?: string | null; created_at?: string | null },
  sortBy: string,
): number {
  switch (sortBy) {
    case "created_asc":
      return createdTime(left) - createdTime(right) || compareNullableText(left.id, right.id);
    case "po_number_asc":
      return compareNullableText(left.po_number, right.po_number) || createdTime(left) - createdTime(right);
    case "po_number_desc":
      return compareNullableText(right.po_number, left.po_number) || createdTime(right) - createdTime(left);
    case "purpose_asc":
      return compareNullableText(left.order_purpose, right.order_purpose) || createdTime(left) - createdTime(right);
    case "purpose_desc":
      return compareNullableText(right.order_purpose, left.order_purpose) || createdTime(right) - createdTime(left);
    case "vendor_asc":
      return compareNullableText(left.vendor_name, right.vendor_name) || createdTime(left) - createdTime(right);
    case "vendor_desc":
      return compareNullableText(right.vendor_name, left.vendor_name) || createdTime(right) - createdTime(left);
    case "created_desc":
    default:
      return createdTime(right) - createdTime(left) || compareNullableText(right.id, left.id);
  }
}

function buildDirectPurchaseOrderFormData(payload: PurchaseOrderCreateDirectPayload): FormData {
  const formData = new globalThis.FormData();
  formData.append("origin", payload.origin);
  formData.append("po_type", payload.po_type);
  formData.append("account_code_id", payload.account_code_id);
  formData.append("vendor_id", payload.vendor_id);
  if (payload.vendor_branch_id) {
    formData.append("vendor_branch_id", payload.vendor_branch_id);
  }
  formData.append("supplier_quotation_ref", payload.supplier_quotation_ref);
  formData.append("delivery_location", payload.delivery_location);
  formData.append("delivery_deadline", payload.delivery_deadline);
  formData.append("payment_terms_code", payload.payment_terms_code);
  formData.append("lead_time", payload.lead_time);
  formData.append("shipping_type", payload.shipping_type);
  formData.append("currency_code", payload.currency_code);
  if (payload.billing_address) {
    formData.append("billing_address", payload.billing_address);
  }
  formData.append("source_description", payload.source_description);
  formData.append("line_items_json", JSON.stringify(payload.line_items));

  if (payload.payment_terms_other) {
    formData.append("payment_terms_other", payload.payment_terms_other);
  }
  if (payload.discount_type) {
    formData.append("discount_type", payload.discount_type);
  }
  if (payload.discount_value) {
    formData.append("discount_value", payload.discount_value);
  }
  if (payload.additional_charges) {
    formData.append("additional_charges", payload.additional_charges);
  }
  if (payload.special_instructions) {
    formData.append("special_instructions", payload.special_instructions);
  }
  if (payload.vendor_contact_email_id) {
    formData.append("vendor_contact_email_id", payload.vendor_contact_email_id);
  }
  if (payload.source_document_path) {
    formData.append("source_document_path", payload.source_document_path);
  }
  if (payload.source_document_name) {
    formData.append("source_document_name", payload.source_document_name);
  }
  if (payload.source_document_file) {
    formData.append("source_document_file", payload.source_document_file);
  }

  return formData;
}

export async function listPurchaseOrderAccountCodeOptions(
  params: PurchaseOrderAccountCodeOptionsParams = {}, signal?: AbortSignal,
): Promise<PurchaseOrderAccountCodeOptionsResponse> {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    throw new Error("Connect to the internet to load PO account codes.");
  }
  const response = await apiClient.get<PurchaseOrderAccountCodeOptionsResponse>(
    "/purchase/purchase-orders/account-code-options/", {
      params: { page: params.page ?? 1, page_size: params.page_size ?? 10,
        ...(params.search?.trim() ? { search: params.search.trim() } : {}),
        ...(params.code?.trim() ? { code: params.code.trim() } : {}),
        ...(params.vessel_id !== undefined ? { vessel_id: params.vessel_id } : {}),
      }, ...(signal ? { signal } : {}),
    },
  );
  return response.data;
}

export async function listPurchaseOrders(
  params: PurchaseOrderListQueryParams = {},
): Promise<PurchaseOrderListResponse> {
  requireOnlineAttentionRegister(params.attention);
  const accountCode = params.account_code?.trim();
  if (accountCode && typeof navigator !== "undefined" && !navigator.onLine) {
    throw new Error("Connect to the internet to filter purchase orders by PO account code.");
  }
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    const { listCachedPurchaseOrders } = await import("../pwa/indexedDB/cacheStore");
    const cachedRecords = await listCachedPurchaseOrders();
    const isInTransitFilter = params.filter === "in-transit";
    const requiresInTransit = isInTransitFilter || params.status === "IN_TRANSIT";
    const search = String(params.purpose ?? (isInTransitFilter ? "" : params.filter) ?? "").trim().toLowerCase();
    const poNumberSearch = String(params.po_number ?? "").trim().toLowerCase();
    const requisitionNumberSearch = String(params.requisition_number ?? "").trim().toLowerCase();
    const sortBy = normalizePurchaseOrderSortBy(params.sort_by);
    const rows = cachedRecords
      .map((record) => record.data)
      .filter((row) =>
        params.status && params.status !== "IN_TRANSIT" ? row.status === params.status : true,
      )
      .filter((row) =>
        requiresInTransit
          ? row.tracker_step === 6 && row.status !== "CLOSED" && row.status !== "CLOSED_WITH_SHORTFALL" && row.status !== "CANCELLED"
          : true,
      )
      .filter((row) => (params.po_type ? row.po_type === params.po_type : true))
      .filter((row) => (params.vessel_id ? String(row.vessel_id) === String(params.vessel_id) : true))
      .filter((row) => (poNumberSearch ? row.po_number.toLowerCase().includes(poNumberSearch) : true))
      .filter((row) =>
        requisitionNumberSearch
          ? String(row.requisition_number ?? "").toLowerCase().includes(requisitionNumberSearch)
          : true,
      )
      .filter((row) => isWithinCreatedDateRange(row, params.date_from, params.date_to))
      .filter((row) => {
        if (!search) {
          return true;
        }
        return [row.po_number, row.requisition_number, row.vendor_name, row.order_purpose]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(search);
      })
      .sort((left, right) => comparePurchaseOrders(left, right, sortBy));
    return {
      data: rows,
      pagination: {
        page: 1,
        page_size: rows.length,
        total_count: rows.length,
        total_pages: 1,
      },
    };
  }

  const requestParams = params.account_code === undefined ? params : { ...params, account_code: accountCode || undefined };
  const response = await apiClient.get<PurchaseOrderListResponse>("/purchase/purchase-orders/", { params: requestParams });
  return response.data;
}

export async function listMyApprovalPurchaseOrders(
  params: PurchaseOrderListQueryParams = {},
): Promise<PurchaseOrderListResponse> {
  const response = await apiClient.get<PurchaseOrderListResponse>("/purchase/purchase-orders/my-approvals/", { params });
  return response.data;
}

export async function getPurchaseOrder(
  purchaseOrderId: string,
  options?: PurchaseOrderReadOptions,
): Promise<DataEnvelope<PurchaseOrderDetail>> {
  const response = options?.invoiceId
    ? await apiClient.get<DataEnvelope<PurchaseOrderDetail>>(`/purchase/purchase-orders/${purchaseOrderId}/`, {
        params: { invoice_id: options.invoiceId },
      })
    : options?.captureCandidate
      ? await apiClient.get<DataEnvelope<PurchaseOrderDetail>>(`/purchase/purchase-orders/${purchaseOrderId}/`, {
          params: { invoice_capture: "1" },
        })
    : await apiClient.get<DataEnvelope<PurchaseOrderDetail>>(`/purchase/purchase-orders/${purchaseOrderId}/`);
  return response.data;
}

export async function createPurchaseOrderFromAwards(
  payload: PurchaseOrderCreateFromAwardsPayload,
  vesselId?: VesselId,
): Promise<DataEnvelope<PurchaseOrderDetail>> {
  const response =
    vesselId !== undefined
      ? await apiClient.post<DataEnvelope<PurchaseOrderDetail>>("/purchase/purchase-orders/", payload, {
          params: { vessel_id: vesselId },
        })
      : await apiClient.post<DataEnvelope<PurchaseOrderDetail>>("/purchase/purchase-orders/", payload);
  return response.data;
}

export interface POCreateBudgetStatus {
  state: "FROZEN" | "NOT_FROZEN" | "NOT_BUDGET_CHECKED" | "UNAVAILABLE";
  account_code: string | null;
  current_spend_usd: string | null;
  pro_rated_to_date_usd: string | null;
  threshold_pct: string | null;
  freeze_trigger_usd: string | null;
  as_of: string | null;
  create_blocked?: boolean;
}

export async function getPOCreateBudgetStatus(
  accountCodeId: string,
  options: { vesselId?: VesselId | null; rfqId?: string | null },
): Promise<DataEnvelope<POCreateBudgetStatus>> {
  const response = await apiClient.get<DataEnvelope<POCreateBudgetStatus>>(
    "/purchase/purchase-orders/create-budget-status/",
    { params: {
      account_code_id: accountCodeId,
      ...(options.rfqId ? { rfq_id: options.rfqId } : { vessel_id: options.vesselId }),
    } },
  );
  return response.data;
}

export async function createDirectPurchaseOrder(
  payload: PurchaseOrderCreateDirectPayload,
  vesselId?: VesselId,
): Promise<DataEnvelope<PurchaseOrderDetail>> {
  const requestBody = payload.source_document_file ? buildDirectPurchaseOrderFormData(payload) : payload;
  const requestConfig = payload.source_document_file
    ? { headers: { "Content-Type": "multipart/form-data" } }
    : undefined;
  const response = vesselId !== undefined
    ? await apiClient.post<DataEnvelope<PurchaseOrderDetail>>("/purchase/purchase-orders/", requestBody, {
        ...requestConfig,
        params: { vessel_id: vesselId },
      })
    : requestConfig
      ? await apiClient.post<DataEnvelope<PurchaseOrderDetail>>(
          "/purchase/purchase-orders/",
          requestBody,
          requestConfig,
        )
      : await apiClient.post<DataEnvelope<PurchaseOrderDetail>>("/purchase/purchase-orders/", requestBody);
  return response.data;
}

function buildPurchaseOrderUpdateFormData(payload: PurchaseOrderUpdatePayload): FormData {
  const formData = new globalThis.FormData();
  const {
    source_document_file: sourceDocumentFile,
    line_items: lineItems,
    new_line_items: newLineItems,
    removed_line_item_ids: removedLineItemIds,
    ...scalarFields
  } = payload;

  Object.entries(scalarFields).forEach(([key, value]) => {
    if (value !== undefined) {
      formData.append(key, String(value));
    }
  });
  if (lineItems !== undefined) {
    formData.append("line_items_json", JSON.stringify(lineItems));
  }
  if (newLineItems !== undefined) {
    formData.append("new_line_items_json", JSON.stringify(newLineItems));
  }
  if (removedLineItemIds !== undefined) {
    formData.append("removed_line_item_ids_json", JSON.stringify(removedLineItemIds));
  }
  if (sourceDocumentFile) {
    formData.append("source_document_file", sourceDocumentFile);
  }

  return formData;
}

function buildPurchaseOrderRevisionFormData(payload: PurchaseOrderRevisePayload): FormData {
  const formData = new globalThis.FormData();
  const {
    source_document_file: sourceDocumentFile,
    line_items: lineItems,
    new_line_items: newLineItems,
    removed_line_item_ids: removedLineItemIds,
    ...scalarFields
  } = payload;

  Object.entries(scalarFields).forEach(([key, value]) => {
    if (value !== undefined) {
      formData.append(key, String(value));
    }
  });
  if (lineItems !== undefined) {
    formData.append("line_items_json", JSON.stringify(lineItems));
  }
  if (newLineItems !== undefined) {
    formData.append("new_line_items_json", JSON.stringify(newLineItems));
  }
  if (removedLineItemIds !== undefined) {
    formData.append("removed_line_item_ids_json", JSON.stringify(removedLineItemIds));
  }
  if (sourceDocumentFile) {
    formData.append("source_document_file", sourceDocumentFile);
  }
  return formData;
}

export async function updatePurchaseOrder(
  purchaseOrderId: string,
  payload: PurchaseOrderUpdatePayload,
): Promise<DataEnvelope<PurchaseOrderDetail>> {
  const requestBody = payload.source_document_file ? buildPurchaseOrderUpdateFormData(payload) : payload;
  const response = payload.source_document_file
    ? await apiClient.patch<DataEnvelope<PurchaseOrderDetail>>(
        `/purchase/purchase-orders/${purchaseOrderId}/`,
        requestBody,
        { headers: { "Content-Type": "multipart/form-data" } },
      )
    : await apiClient.patch<DataEnvelope<PurchaseOrderDetail>>(
        `/purchase/purchase-orders/${purchaseOrderId}/`,
        requestBody,
      );
  return response.data;
}

export async function submitPurchaseOrder(
  purchaseOrderId: string,
  payload: PurchaseOrderSubmitPayload = {},
): Promise<DataEnvelope<PurchaseOrderDetail>> {
  const response = await apiClient.post<DataEnvelope<PurchaseOrderDetail>>(
    `/purchase/purchase-orders/${purchaseOrderId}/submit/`,
    payload,
  );
  return response.data;
}

export async function sendPurchaseOrderToVendor(
  purchaseOrderId: string,
  senderEmail: string,
  remarks: string,
): Promise<DataEnvelope<PurchaseOrderDetail>> {
  const response = await apiClient.post<DataEnvelope<PurchaseOrderDetail>>(
    `/purchase/purchase-orders/${purchaseOrderId}/send-to-vendor/`,
    { sender_email: senderEmail, remarks },
  );
  return response.data;
}

export async function resendPurchaseOrderToVendor(
  purchaseOrderId: string,
  senderEmail: string,
  reason: string,
): Promise<DataEnvelope<PurchaseOrderDetail>> {
  if (globalThis.navigator && !globalThis.navigator.onLine) {
    throw new Error("Resend requires an online Office session.");
  }
  const response = await apiClient.post<DataEnvelope<PurchaseOrderDetail>>(
    `/purchase/purchase-orders/${purchaseOrderId}/resend-to-vendor/`,
    { sender_email: senderEmail, reason },
  );
  return response.data;
}

export async function markPurchaseOrderDispatchNotRequired(
  purchaseOrderId: string,
  reason: string,
): Promise<DataEnvelope<PurchaseOrderDetail>> {
  if (globalThis.navigator && !globalThis.navigator.onLine) {
    throw new Error("No vendor copy requires an online Office session.");
  }
  const response = await apiClient.post<DataEnvelope<PurchaseOrderDetail>>(
    `/purchase/purchase-orders/${purchaseOrderId}/dispatch-not-required/`, { reason },
  );
  return response.data;
}

export async function withdrawPurchaseOrder(
  purchaseOrderId: string,
  reason: string,
): Promise<DataEnvelope<PurchaseOrderDetail>> {
  if (globalThis.navigator && !globalThis.navigator.onLine) {
    throw new Error("Withdrawal requires an online Office session.");
  }
  const response = await apiClient.post<DataEnvelope<PurchaseOrderDetail>>(
    `/purchase/purchase-orders/${purchaseOrderId}/withdraw/`, { reason },
  );
  return response.data;
}

export async function cancelPurchaseOrder(
  purchaseOrderId: string,
  reason: string,
  senderEmail?: string,
): Promise<DataEnvelope<PurchaseOrderDetail>> {
  if (globalThis.navigator && !globalThis.navigator.onLine) {
    throw new Error("Cancellation requires an online Office session.");
  }
  const response = await apiClient.post<DataEnvelope<PurchaseOrderDetail>>(
    `/purchase/purchase-orders/${purchaseOrderId}/cancel/`,
    { reason, ...(senderEmail ? { sender_email: senderEmail } : {}) },
  );
  return response.data;
}

export async function listPOBudgetResiduals(
  params: POBudgetResidualListParams,
): Promise<POBudgetResidualListResponse> {
  const response = await apiClient.get<POBudgetResidualListResponse>(
    "/purchase/purchase-orders/budget-residuals/",
    { params },
  );
  return response.data;
}

export async function exportPOBudgetResidualsPdf(
  params: POBudgetResidualListParams,
): Promise<void> {
  const response = await apiClient.get<Blob>(
    "/purchase/purchase-orders/budget-residuals/export/pdf/",
    { params, responseType: "blob" },
  );
  downloadBlob(response.data, "budget-residuals.pdf");
}

export async function exportPOBudgetResidualsExcel(
  params: POBudgetResidualListParams,
): Promise<void> {
  const response = await apiClient.get<Blob>(
    "/purchase/purchase-orders/budget-residuals/export/excel/",
    { params, responseType: "blob" },
  );
  downloadBlob(response.data, "budget-residuals.xlsx");
}

export async function cancelAndRestartPurchaseOrder(
  purchaseOrderId: string,
  reason: string,
  senderEmail?: string,
): Promise<PurchaseOrderCancelAndRestartResult> {
  if (globalThis.navigator && !globalThis.navigator.onLine) {
    throw new Error("Cancellation and restart requires an online Office session.");
  }
  const response = await apiClient.post<PurchaseOrderCancelAndRestartResult>(
    `/purchase/purchase-orders/${purchaseOrderId}/cancel-and-restart/`,
    { reason, ...(senderEmail ? { sender_email: senderEmail } : {}) },
  );
  return response.data;
}

export async function approvePurchaseOrder(
  purchaseOrderId: string,
  payload: PurchaseOrderApprovePayload,
): Promise<DataEnvelope<PurchaseOrderDetail>> {
  if (globalThis.navigator && !globalThis.navigator.onLine) {
    throw new Error("Approval requires an online Office session.");
  }
  const response = await apiClient.post<DataEnvelope<PurchaseOrderDetail>>(
    `/purchase/purchase-orders/${purchaseOrderId}/approve/`,
    payload,
  );
  return response.data;
}

export async function rejectPurchaseOrder(
  purchaseOrderId: string,
  payload: PurchaseOrderRejectPayload,
): Promise<DataEnvelope<PurchaseOrderDetail>> {
  if (globalThis.navigator && !globalThis.navigator.onLine) {
    throw new Error("Rejection requires an online Office session.");
  }
  const response = await apiClient.post<DataEnvelope<PurchaseOrderDetail>>(
    `/purchase/purchase-orders/${purchaseOrderId}/reject/`,
    payload,
  );
  return response.data;
}

export async function revisePurchaseOrder(
  purchaseOrderId: string,
  payload: PurchaseOrderRevisePayload,
): Promise<DataEnvelope<PurchaseOrderRevisionSummary>> {
  const requestBody = payload.source_document_file ? buildPurchaseOrderRevisionFormData(payload) : payload;
  const response = payload.source_document_file
    ? await apiClient.post<DataEnvelope<PurchaseOrderRevisionSummary>>(
        `/purchase/purchase-orders/${purchaseOrderId}/revise/`,
        requestBody,
        { headers: { "Content-Type": "multipart/form-data" } },
      )
    : await apiClient.post<DataEnvelope<PurchaseOrderRevisionSummary>>(
        `/purchase/purchase-orders/${purchaseOrderId}/revise/`,
        requestBody,
      );
  return response.data;
}

export async function abandonPurchaseOrderRevision(
  purchaseOrderId: string,
): Promise<DataEnvelope<PurchaseOrderRevisionSummary>> {
  const response = await apiClient.post<DataEnvelope<PurchaseOrderRevisionSummary>>(
    `/purchase/purchase-orders/${purchaseOrderId}/revise/abandon/`,
    {},
  );
  return response.data;
}

export async function openPurchaseOrderPdf(purchaseOrderId: string, versionNumber?: number): Promise<void> {
  const search = versionNumber ? `?version_number=${versionNumber}` : "";
  try {
    const response = await apiClient.get<Blob>(`/purchase/purchase-orders/${purchaseOrderId}/pdf/${search}`, {
      responseType: "blob",
    });
    openBlobInNewTab(response.data);
  } catch (error) {
    if (axios.isAxiosError<unknown>(error) && error.response?.data instanceof globalThis.Blob &&
        error.response.data.type.includes("json")) {
      try {
        // PDF requests receive structured JSON errors as blobs as well.
        const payload: unknown = JSON.parse(await error.response.data.text());
        error.response.data = payload;
      } catch {
        // Preserve the original request failure if the error body cannot be decoded.
        throw error;
      }
    }
    throw error;
  }
}

export async function viewPurchaseOrderQuotationAttachment(
  purchaseOrderId: string,
  attachmentId: string,
): Promise<void> {
  const response = await apiClient.get<Blob>(
    `/purchase/purchase-orders/${purchaseOrderId}/quotation-attachments/${attachmentId}/view/`,
    { responseType: "blob" },
  );
  openBlobInNewTab(response.data);
}

export async function downloadPurchaseOrderQuotationAttachment(
  purchaseOrderId: string,
  attachmentId: string,
  fileName: string,
): Promise<void> {
  const response = await apiClient.get<Blob>(
    `/purchase/purchase-orders/${purchaseOrderId}/quotation-attachments/${attachmentId}/download/`,
    { responseType: "blob" },
  );
  const downloadUrl = window.URL.createObjectURL(response.data);
  const anchor = document.createElement("a");
  anchor.href = downloadUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(downloadUrl);
}

export async function openPurchaseOrderSourceDocument(
  purchaseOrderId: string,
  options?: PurchaseOrderSourceDocumentReadOptions,
): Promise<void> {
  const params = options?.invoiceId
    ? { invoice_id: options.invoiceId }
    : options?.captureCandidate
      ? { invoice_capture: "1" }
      : undefined;
  const requestConfig = params ? { params, responseType: "blob" as const } : { responseType: "blob" as const };
  const response = await apiClient.get<Blob>(
    `/purchase/purchase-orders/${purchaseOrderId}/source-document/`,
    requestConfig,
  );
  openBlobInNewTab(response.data, options?.fileName);
}

export async function openAdvancePaymentMemo(purchaseOrderId: string): Promise<void> {
  const response = await apiClient.get<Blob>(`/purchase/purchase-orders/${purchaseOrderId}/advance-payment-memo/`, {
    responseType: "blob",
  });
  openBlobInNewTab(response.data);
}

export async function createPurchaseOrderComment(
  purchaseOrderId: string,
  payload: PurchaseOrderCommentCreatePayload,
): Promise<DataEnvelope<PurchaseOrderComment>> {
  const requestBody = hasPurchaseOrderCommentAttachmentFiles(payload)
    ? buildPurchaseOrderCommentFormData(payload)
    : payload;
  const response = await apiClient.post<DataEnvelope<PurchaseOrderComment>>(
    `/purchase/purchase-orders/${purchaseOrderId}/comments/`,
    requestBody,
  );
  return response.data;
}

function hasPurchaseOrderCommentAttachmentFiles(payload: PurchaseOrderCommentCreatePayload): boolean {
  return Array.isArray(payload.attachment_files) && payload.attachment_files.length > 0;
}

function buildPurchaseOrderCommentFormData(payload: PurchaseOrderCommentCreatePayload): FormData {
  const formData = new globalThis.FormData();
  formData.set("text", payload.text);
  if (payload.parent_id !== undefined && payload.parent_id !== null) {
    formData.set("parent_id", payload.parent_id);
  }
  if (payload.client_comment_id) {
    formData.set("client_comment_id", payload.client_comment_id);
  }
  if (payload.mentioned_recipients?.length) {
    formData.set("mentioned_recipients", JSON.stringify(payload.mentioned_recipients));
  }
  payload.attachment_files?.forEach((file) => {
    formData.append("attachment_files", file);
  });
  return formData;
}

function apiPathFromDownloadUrl(downloadUrl: string): string {
  return downloadUrl.startsWith("/api/") ? downloadUrl.slice(4) : downloadUrl;
}

async function fetchCommentAttachmentBlob(downloadUrl: string): Promise<Blob> {
  const response = await apiClient.get<Blob>(apiPathFromDownloadUrl(downloadUrl), {
    responseType: "blob",
  });
  return response.data;
}

export async function openPurchaseOrderCommentAttachment(downloadUrl: string): Promise<void> {
  openBlobInNewTab(await fetchCommentAttachmentBlob(downloadUrl));
}

export async function downloadPurchaseOrderCommentAttachment(
  downloadUrl: string,
  originalFilename: string,
): Promise<void> {
  downloadBlob(await fetchCommentAttachmentBlob(downloadUrl), originalFilename);
}

export function getPurchaseOrderPdfUrl(purchaseOrderId: string, versionNumber?: number): string {
  const search = versionNumber ? `?version_number=${versionNumber}` : "";
  return `/api/purchase/purchase-orders/${purchaseOrderId}/pdf/${search}`;
}
